export enum EBrowser {
	Chrome = "Chrome",
	Firefox = "Firefox",
	Safari = "Safari",
	Edg = "Edg",
	InternetExplorer = "Internet Explorer",
	Unknown = "Unknown",
}

type TBrowserDetails = {
	browser: EBrowser;
	browserName: string;
	browserVersion: string;
};

const UNKNOWN_BROWSER_VERSION = "Unknown";

const BROWSER_NAMES: Record<EBrowser, string> = {
	[EBrowser.Chrome]: "Chrome",
	[EBrowser.Firefox]: "Firefox",
	[EBrowser.Safari]: "Safari",
	[EBrowser.Edg]: "Edge",
	[EBrowser.InternetExplorer]: "Internet Explorer",
	[EBrowser.Unknown]: "Unknown",
};

const BROWSER_VERSION_PATTERNS: Record<EBrowser, RegExp[]> = {
	[EBrowser.Chrome]: [/(?:Chrome|CriOS)\/([\d.]+)/],
	[EBrowser.Firefox]: [/(?:Firefox|FxiOS)\/([\d.]+)/],
	[EBrowser.Safari]: [/Version\/([\d.]+)/],
	[EBrowser.Edg]: [/(?:Edg|EdgA|EdgiOS)\/([\d.]+)/],
	[EBrowser.InternetExplorer]: [/MSIE ([\d.]+)/, /rv:([\d.]+)/],
	[EBrowser.Unknown]: [],
};

const getUserAgent = () => {
	if (typeof navigator === "undefined") {
		return "";
	}

	return navigator.userAgent;
};

const getDetailedBrowserByUserAgent = (userAgent: string): EBrowser => {
	if (/(?:Edg|EdgA|EdgiOS)\//.test(userAgent)) {
		return EBrowser.Edg;
	}

	if (/(?:Firefox|FxiOS)\//.test(userAgent)) {
		return EBrowser.Firefox;
	}

	if (/(?:Chrome|CriOS)\//.test(userAgent)) {
		return EBrowser.Chrome;
	}

	if (userAgent.includes(EBrowser.Safari) && !/(?:Chrome|CriOS|Edg|EdgA|EdgiOS)\//.test(userAgent)) {
		return EBrowser.Safari;
	}

	if (userAgent.includes("MSIE") || userAgent.includes("Trident")) {
		return EBrowser.InternetExplorer;
	}

	return EBrowser.Unknown;
};

const getCurrentBrowserByUserAgent = (userAgent: string): EBrowser => {
	if (userAgent.includes(EBrowser.Chrome) && !userAgent.includes(EBrowser.Edg)) {
		return EBrowser.Chrome;
	}

	if (userAgent.includes(EBrowser.Firefox)) {
		return EBrowser.Firefox;
	}

	if (userAgent.includes(EBrowser.Safari) && !userAgent.includes(EBrowser.Chrome)) {
		return EBrowser.Safari;
	}

	if (userAgent.includes(EBrowser.Edg)) {
		return EBrowser.Edg;
	}

	if (userAgent.includes("MSIE") || userAgent.includes("Trident")) {
		return EBrowser.InternetExplorer;
	}

	return EBrowser.Unknown;
};

const getBrowserVersion = (userAgent: string, browser: EBrowser) => {
	const patterns = BROWSER_VERSION_PATTERNS[browser];

	for (const pattern of patterns) {
		const version = userAgent.match(pattern)?.[1];

		if (version) {
			return version;
		}
	}

	return UNKNOWN_BROWSER_VERSION;
};

export const getBrowserDetails = (): TBrowserDetails => {
	const userAgent = getUserAgent();
	const browser = getDetailedBrowserByUserAgent(userAgent);

	return {
		browser,
		browserName: BROWSER_NAMES[browser],
		browserVersion: getBrowserVersion(userAgent, browser),
	};
};

export const getBrowserInfo = (): EBrowser => getCurrentBrowserByUserAgent(getUserAgent());
